import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import { aggregateBuyers, computeFlightPayouts } from '../lib/data.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'

// Public buyer-facing summary, scanned from the QR a buyer is handed at the
// auction. Read-only: shows their teams, total owed, paid status. No login.
//
//   /calcutta?t={tournamentId}&buyer={buyerKey}
//
export default function CalcuttaApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')
  const buyerKey = params.get('buyer')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the QR from your auction sheet.')
      return
    }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  const { buyer, teamLookup } = useMemo(() => {
    if (!tournament || !buyerKey) return { buyer: null, teamLookup: {} }
    const bids = tournament.calcutta?.bids || {}
    const paid = tournament.calcutta?.paid || {}
    const all = aggregateBuyers(bids, paid)
    const me = all.find((b) => b.buyerKey === buyerKey) || null

    // Build teamId → { teamName, flightName } so we can render owned teams
    // with friendly labels and not just IDs.
    const lookup = {}
    for (const [fid, f] of Object.entries(tournament.flights || {})) {
      for (const [tid, t] of Object.entries(f.teams || {})) {
        lookup[tid] = { teamName: t.info?.name || tid, flightName: f.info?.name || fid, flightId: fid }
      }
    }
    return { buyer: me, teamLookup: lookup }
  }, [tournament, buyerKey])

  const settings = tournament?.calcutta?.settings || {}
  const enabled = !!settings.enabled

  return (
    <div className="min-h-full" style={{ backgroundColor: CLUB.backgroundColor }}>
      <PageNav back tone="gold" />
      <div className="max-w-md mx-auto px-4 pt-16 pb-10">
        {error ? (
          <ErrorCard message={error} />
        ) : !tournament ? (
          <p className="text-white/70 text-center mt-10">Loading…</p>
        ) : !enabled ? (
          <ErrorCard message="The Calcutta auction for this tournament is not active." />
        ) : !buyer ? (
          <ErrorCard message={`No Calcutta entries found for "${buyerKey}". The buyer key may be wrong, or no bids are recorded yet.`} />
        ) : (
          <BuyerSummary buyer={buyer} teamLookup={teamLookup} tournament={tournament} settings={settings} />
        )}
      </div>
    </div>
  )
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-2xl p-5 mt-10 text-center shadow-xl">
      <div className="text-3xl mb-2">⚠️</div>
      <p className="text-sm text-gray-700">{message}</p>
    </div>
  )
}

function BuyerSummary({ buyer, teamLookup, tournament, settings }) {
  const buyBackEnabled = !!settings.buyBackEnabled
  // TEST: per-flight model — show what each of the buyer's teams is playing for
  // in ITS flight (pot − MGA, split 70/30 to 1st/2nd).
  const perFlight = PBCC_PREMIUM
  const { flightPayById } = useMemo(() => {
    if (!perFlight) return { flightPayById: {} }
    const arr = Object.entries(tournament.flights || {}).map(([id, f]) => ({ id, ...f }))
    const mga = Number.isFinite(Number(settings.mgaPct)) ? Number(settings.mgaPct) : 10
    const structure = Array.isArray(settings.payoutStructure) && settings.payoutStructure.length
      ? settings.payoutStructure : [{ place: 1, pct: 70 }, { place: 2, pct: 30 }]
    const fp = computeFlightPayouts(arr, tournament.calcutta?.bids || {}, { mgaPct: mga, payoutStructure: structure, buyBackEnabled })
    const m = {}
    for (const f of fp) m[f.flightId] = f
    return { flightPayById: m, mgaPct: mga, splits: structure }
  }, [perFlight, tournament, settings, buyBackEnabled])
  return (
    <>
      <div className="text-center text-white">
        <h1 className="text-xl font-extrabold tracking-widest uppercase">Calcutta Summary</h1>
        <p className="text-sm opacity-75 mt-1">{tournament.info?.name}</p>
      </div>

      <div className="bg-white rounded-2xl p-5 mt-4 shadow-xl">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Buyer</div>
        <div className="text-2xl font-extrabold">{buyer.buyerName}</div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Total Owed</div>
            <div className="text-2xl font-extrabold text-green-deep">${buyer.totalOwed.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Status</div>
            {buyer.paid ? (
              <div className="inline-block mt-1 px-3 py-1 rounded-full bg-green-mint text-green-deep font-extrabold text-sm">✓ Paid</div>
            ) : (
              <div className="inline-block mt-1 px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-sm">Outstanding</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 mt-4 shadow-xl">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">
          Your Teams ({buyer.teams.length})
        </div>
        <ul className="divide-y divide-gray-100">
          {buyer.teams.map((line) => {
            const meta = teamLookup[line.teamId] || {}
            return (
              <li key={line.teamId} className="py-2.5">
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{meta.teamName || line.teamId}</div>
                    <div className="text-xs text-gray-500">{meta.flightName || '—'}</div>
                    {perFlight && flightPayById[meta.flightId] ? (
                      <div className="text-xs text-green-deep font-semibold mt-0.5">
                        Plays for {flightPayById[meta.flightId].payouts.map((p) => `${p.place === 1 ? '1st' : p.place === 2 ? '2nd' : `${p.place}th`} $${p.amount.toLocaleString()}`).join(' · ')}
                      </div>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${line.bidAmount.toLocaleString()}</div>
                    {buyBackEnabled && line.buyBack ? (
                      <div className="text-xs text-green-deep">+${line.buyBackCost.toLocaleString()} buy-back</div>
                    ) : null}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {perFlight ? (
        <p className="text-center text-white/70 text-[11px] mt-3 px-4">
          Payouts shown are net — each team plays for its flight's 1st / 2nd place.
        </p>
      ) : null}

      {!buyer.paid ? (
        <p className="text-center text-white/80 text-xs mt-4">
          Pay the auction organizer to settle up. Status updates here once they mark you paid.
        </p>
      ) : (
        <p className="text-center text-white/80 text-xs mt-4">
          Thanks — you're all settled. Good luck to your teams!
        </p>
      )}
    </>
  )
}
